import { createAsyncThunk } from '@reduxjs/toolkit';
import { logger } from 'common/services';
import { selectMyCommunityId } from 'community/api';
import { orderBy } from 'lodash-es';
import { addAppErrorMessage } from 'modules/alerts/state';
import { RootState } from 'store';
import { eventApi, eventSeriesApi } from 'store/apis/event';

import { EventWizardFormSerializedValues } from '../../validation';
import { CreateEventEntitiesArgs } from '../types';
import { updateImages } from './updateImages';
import { updateQuestions } from './updateQuestions';
import { updateTagCategories } from './updateTagCategories';

export const createEvent = createAsyncThunk<{ eventId: number }, EventWizardFormSerializedValues>(
  '[Event Wizard] Create Event',
  async (formValues, { dispatch }) => {
    const { eventId, eventIds, seriesId } = await dispatch(initializeNewEvent(formValues)).unwrap();
    await Promise.all([
      dispatch(createEventEntities({ formValues, eventId, seriesId })).unwrap(),
      dispatch(updateTagCategories({ eventIds, values: formValues })).unwrap(),
    ]);
    return { eventId };
  }
);

const initializeNewEvent = createAsyncThunk<
  { eventId: number; eventIds: number[]; seriesId?: number },
  EventWizardFormSerializedValues
>('[Event Wizard] Initialize New Event', async (payload, { dispatch, getState }) => {
  const communityId = selectMyCommunityId(getState() as RootState);

  if (!communityId) {
    dispatch(addAppErrorMessage('Error occurred while creating an Event'));
    logger.error('Community ID missing while creating an Event');
    throw new Error('Community ID missing while creating an Event');
  }

  if (payload.schedule.length > 1) {
    const { events, id } = await dispatch(
      eventSeriesApi.endpoints.createEventSeries.initiate({
        ...payload,
        schedule: orderBy(payload.schedule, 'startTime', 'asc'),
        relatedImageIds: [],
        communityId,
      })
    ).unwrap();
    return { eventId: events[0].id, eventIds: events.map((e) => e.id), seriesId: id };
  }

  const { id } = await dispatch(
    eventApi.endpoints.createEvent.initiate({ ...payload, relatedImageIds: [], communityId })
  ).unwrap();
  return { eventId: id, eventIds: [id] };
});

export const createEventEntities = createAsyncThunk<void, CreateEventEntitiesArgs>(
  '[Event Wizard] Create Event Entities',
  async ({ formValues, eventId, seriesId }, { dispatch }) => {
    await dispatch(updateQuestions({ eventId, seriesId, values: formValues })).unwrap();
    await dispatch(updateImages({ eventId, seriesId, values: formValues })).unwrap();
  }
);
